import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson, ApiError, BoundSource } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { useLayer } from '../../components/layers'
import dlg from '../../components/layers.module.css'
import { modeLabel } from '../TeamsPage'
import { ClientsResponse, dateText, EquipmentMove, EquipmentRow, LicenceResponse, LicenceTerms, licenceText, SetupAnswers, SetupQuestion, SetupResponse } from './api'
import styles from './GodMode.module.css'
import s from './ClientSetupPage.module.css'

// Client Setup: what this client uses, page by page, and the equipment
// Dataphyll has lent it. Editable any time. The catalogue (ways of working,
// questions, equipment) comes from the API (services/client-setup.js).
//
// Product, Stock & Dispatch, season start and clock-off are asked on the
// first page but saved through Client Setup's own settings routes for this
// client, with their own refusals. The rest is saved whole, against the
// Setup as it was opened. The licence has its own Save; equipment rows are
// saved as each is added.

interface TenantBits {
  product?: 'grow' | 'packright' | null
  products?: { k: 'grow' | 'packright'; label: string }[]
  modules?: { stock_dispatch: { licence: 'paid' | 'included' | null } }
  licences?: ('paid' | 'included')[]
  season?: { override: string | null; inferred: string | null }
  last_pick_is_clock_off?: boolean
}

interface General {
  product: string
  licence: string
  season: 'inferred' | 'date'
  seasonDate: string
  clockOff: boolean
}

const today = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const firstAnswer = (q: SetupQuestion) => (q.def !== undefined ? q.def : q.multi ? [q.opts[0]] : q.opts[0])

const shown = (q: SetupQuestion, answers: SetupAnswers) => {
  if (!q.showIf) return true
  const v = answers[q.showIf[0]]
  return Array.isArray(v) ? v.includes(q.showIf[1]) : v === q.showIf[1]
}

const generalOf = (t: TenantBits): General => ({
  product: t.product || '',
  licence: t.modules?.stock_dispatch.licence || '',
  season: t.season?.override ? 'date' : 'inferred',
  seasonDate: t.season?.override || today(),
  clockOff: !!t.last_pick_is_clock_off,
})

export function ClientSetupPage() {
  const { id = '' } = useParams()
  const { user } = useAuth()
  const [data, setData] = useState<SetupResponse | null>(null)
  const [bits, setBits] = useState<TenantBits | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uses, setUses] = useState<string[]>([])
  const [answers, setAnswers] = useState<SetupAnswers>({})
  const [retainFrom, setRetainFrom] = useState(today())
  const [general, setGeneral] = useState<General | null>(null)
  const [step, setStep] = useState(0)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [others, setOthers] = useState<{ id: string; name: string }[]>([])
  const [copyFrom, setCopyFrom] = useState('')
  // The question being asked, and the action waiting on its answer.
  const [ask, setAsk] = useState<null | { kind: 'unsaved' | 'save' | 'copy' | 'leave'; then?: () => void }>(null)
  // The changes "Not now" was answered for: moving on is not asked again until there are others.
  const [waved, setWaved] = useState<string | null>(null)
  const navigate = useNavigate()
  // What was last loaded, to tell what this save changes.
  const [base, setBase] = useState<{ uses: string[]; answers: SetupAnswers; retainFrom: string; general: General } | null>(null)

  // The settings routes act on the workspace X-Tenant-Id names, so each is pinned to this client.
  const bound: BoundSource | null = useMemo(
    () => (user ? { getIdToken: () => user.getIdToken(), uid: user.uid, boundTenantId: id } : null),
    [user, id]
  )

  const fill = useCallback((d: SetupResponse) => {
    const qs = [...d.catalogue.general_qs, ...Object.values(d.catalogue.steps).flatMap((st) => st.qs)]
    const a: SetupAnswers = {}
    for (const q of qs) a[q.id] = firstAnswer(q)
    const v = { uses: d.setup?.uses || [], answers: { ...a, ...(d.setup?.answers || {}) }, retainFrom: d.setup ? d.setup.retain_from || '' : today() }
    setUses(v.uses)
    setAnswers(v.answers)
    setRetainFrom(v.retainFrom)
    return v
  }, [])

  const load = useCallback(async () => {
    try {
      const [d, t] = await Promise.all([
        apiJson<SetupResponse>(user, `/api/god-mode/clients/${id}/setup`),
        apiJson<TenantBits>(bound, '/api/settings/tenant'),
      ])
      setData(d)
      setBits(t)
      setGeneral(generalOf(t))
      setBase({ ...fill(d), general: generalOf(t) })
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 404) setNotFound(true)
      else setError(e.message)
    }
  }, [user, bound, id, fill])

  useEffect(() => {
    load()
  }, [load])

  // The Stock & Dispatch licence saved on the Licence page: what General compares against moves with it.
  const tookStockDispatch = useCallback((l: 'paid' | 'included' | null) => {
    setBits((b) => (b ? { ...b, modules: { stock_dispatch: { licence: l } } } : b))
    setGeneral((g) => (g ? { ...g, licence: l || '' } : g))
    setBase((b) => (b ? { ...b, general: { ...b.general, licence: l || '' } } : b))
  }, [])

  useEffect(() => {
    let live = true
    apiJson<ClientsResponse>(user, '/api/god-mode/clients')
      .then((d) => live && setOthers(d.clients.filter((c) => c.id !== id).map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [user, id])

  const cat = data?.catalogue
  const packright = general?.product === 'packright'
  const on = useCallback((u: string) => u === 'control' || (u === 'p_devices' ? packright : uses.includes(u)), [uses, packright])

  const steps = useMemo(() => {
    if (!cat) return []
    const list: { id: string; label: string; group: string }[] = [
      { id: 'general', label: 'Product and modules', group: 'General' },
      { id: 'uses', label: 'Ways of working', group: 'What they use' },
    ]
    for (const u of cat.uses) if (on(u.id) && cat.steps[u.id]) list.push({ id: u.id, label: cat.steps[u.id].title || u.label, group: u.group })
    list.push({ id: 'licence', label: 'Licence and work order', group: 'Licence' })
    if (cat.equipment.some((e) => on(e.needs))) list.push({ id: 'equipment', label: 'Bundled equipment', group: 'Equipment' })
    list.push({ id: 'review', label: 'Review', group: 'Finish' })
    return list
  }, [cat, on])

  if (notFound) {
    return (
      <>
        <PageHeader title="Client not found" />
        <PageBody>
          <p className={styles.lead}>
            No client of yours has this address. <Link to="/god-mode/clients">Back to clients</Link>
          </p>
        </PageBody>
      </>
    )
  }
  if (!data || !cat || !general || !bits) {
    return (
      <>
        <PageHeader title="Setup" />
        <PageBody>{error ? <p className={styles.error}>{error}</p> : <p className={styles.lead}>Loading…</p>}</PageBody>
      </>
    )
  }

  const label = (u: string) => cat.uses.find((x) => x.id === u)?.label || u
  const productLabel = (k: string) => bits.products?.find((p) => p.k === k)?.label || 'Not set'
  const changes: string[] = []
  if (!data.setup) changes.push('First setup: from now on team setup offers only the modes and pay this client has turned on.')
  if (base) {
    const b = base.general
    if (general.product !== b.product) changes.push(`Product becomes ${productLabel(general.product)}: screens appear or go for everyone at this client.`)
    const lic = (g: General) => (g.product === 'packright' ? g.licence : '')
    if (lic(general) !== lic(b)) changes.push(lic(general) ? `Stock & Dispatch becomes ${licenceText(lic(general))}.` : 'Stock & Dispatch is turned off: its screens go.')
    const off = base.uses.filter((u) => !uses.includes(u))
    const added = uses.filter((u) => !base.uses.includes(u))
    if (added.length) changes.push(`Turned on: ${added.map(label).join(', ')}.`)
    if (off.length) changes.push(`Turned off: ${off.map(label).join(', ')}. New teams can no longer choose them; existing teams keep what they have.`)
    if (JSON.stringify(answers) !== JSON.stringify(base.answers)) changes.push('Answers changed: what team setup offers may change.')
    if (retainFrom !== base.retainFrom) changes.push(retainFrom ? `Data before ${dateText(retainFrom)} is left out of reports.` : 'Retain data from is cleared: all data counts.')
    if (general.season !== b.season || (general.season === 'date' && general.seasonDate !== b.seasonDate)) changes.push('Season start changes: Reports count a different season.')
    if (general.clockOff !== b.clockOff) changes.push(general.clockOff ? "A worker's last preassign now clocks them off." : "A worker's last preassign no longer clocks them off.")
  }
  const dirty = changes.length > 0
  const edited = changes.length > (data.setup ? 0 : 1)
  // Moving to another page with changes nobody saved asks first: save them (and say what that does), or not now.
  const go = (i: number) => {
    if (edited && waved !== JSON.stringify(changes)) setAsk({ kind: 'unsaved', then: () => setStep(i) })
    else setStep(i)
  }

  const cur = Math.min(step, steps.length - 1)
  const curId = steps[cur].id
  const last = cur === steps.length - 1

  const tick = (u: string, v: boolean) => {
    const link = cat.uses.find((x) => x.id === u)?.link
    setUses((list) => {
      const next = new Set(list)
      for (const x of [u, ...(link ? [link] : [])]) v ? next.add(x) : next.delete(x)
      return [...next]
    })
    setSaved(null)
  }
  const answer = (q: string, v: string | string[]) => {
    setAnswers((a) => ({ ...a, [q]: v }))
    setSaved(null)
  }
  const setGen = (patch: Partial<General>) => {
    setGeneral((g) => (g ? { ...g, ...patch } : g))
    setSaved(null)
  }

  const copy = async () => {
    if (!copyFrom) return
    setError(null)
    try {
      const d = await apiJson<SetupResponse>(user, `/api/god-mode/clients/${copyFrom}/setup`)
      if (!d.setup) {
        setError(`${d.client.name} hasn't been set up yet: nothing to copy.`)
        return
      }
      setUses(d.setup.uses)
      setAnswers((a) => ({ ...a, ...d.setup!.answers }))
      setSaved(`Copied ${d.client.name}’s ways of working and answers. Nothing is saved until you save.`)
    } catch (e: any) {
      setError(e.message)
    }
  }

  // The setup itself, then each of the client's own settings that changed,
  // in an order their routes accept: a licence comes off before the product
  // leaves PackRight, and goes on once it is PackRight.
  const save = async () => {
    setBusy(true)
    setError(null)
    setSaved(null)
    const was = generalOf(bits)
    const g = general
    const wantLicence = g.product === 'packright' ? g.licence : ''
    const put = (path: string, body: object) => apiJson(bound, path, { method: 'PUT', body })
    try {
      await apiJson(user, `/api/god-mode/clients/${id}/setup`, {
        method: 'PUT',
        body: { uses, answers, retain_from: retainFrom || null, before: data.setup?.updated_at ?? null },
      })
      if (was.licence && !wantLicence) await put('/api/settings/tenant/licence', { licence: null, before: was.licence })
      if (g.product && g.product !== was.product) await put('/api/settings/tenant/product', { product: g.product, before: was.product || null })
      if (wantLicence && wantLicence !== was.licence) await put('/api/settings/tenant/licence', { licence: wantLicence, before: was.licence || null })
      const seasonNext = g.season === 'date' ? g.seasonDate : null
      if (seasonNext !== (bits.season?.override ?? null)) await put('/api/settings/tenant/season', { season_start: seasonNext, before: bits.season?.override ?? null })
      if (g.clockOff !== was.clockOff) await put('/api/settings/tenant/clock-off', { last_pick_is_clock_off: g.clockOff, before: was.clockOff })
      await load()
      setSaved('Setup saved. Existing teams are unchanged; a team using something now turned off keeps it.')
      return true
    } catch (e: any) {
      const b = e instanceof ApiError ? e.body : null
      setError(b?.code === 'STALE' ? 'Someone else saved this client’s setup since you opened it. Reload to see theirs.' : e.message)
      // What did save is shown as saved.
      try {
        const [d, t] = await Promise.all([apiJson<SetupResponse>(user, `/api/god-mode/clients/${id}/setup`), apiJson<TenantBits>(bound, '/api/settings/tenant')])
        setData(d)
        setBits(t)
      } catch {}
      return false
    } finally {
      setBusy(false)
    }
  }

  const question = (q: SetupQuestion) => (
    <fieldset key={q.id} className={s.question}>
      <legend>{q.label}</legend>
      {q.hint && <p className={styles.hint}>{q.hint}</p>}
      <div className={s.options}>
        {q.opts.map((o) => {
          const v = answers[q.id]
          const checked = q.multi ? Array.isArray(v) && v.includes(o) : v === o
          return (
            <label key={o} className={checked ? `${s.option} ${s.chosen}` : s.option}>
              <input
                type={q.multi ? 'checkbox' : 'radio'}
                name={q.id}
                checked={checked}
                onChange={(e) => {
                  if (!q.multi) return answer(q.id, o)
                  const list = Array.isArray(v) ? v : []
                  answer(q.id, q.opts.filter((x) => (x === o ? e.target.checked : list.includes(x))))
                }}
              />
              {o}
            </label>
          )
        })}
      </div>
    </fieldset>
  )

  const pills = (name: string, items: readonly (readonly [string, string])[], value: string, pick: (k: string) => void) => (
    <div className={s.options}>
      {items.map(([k, label]) => (
        <label key={k} className={value === k ? `${s.option} ${s.chosen}` : s.option}>
          <input type="radio" name={name} checked={value === k} onChange={() => pick(k)} />
          {label}
        </label>
      ))}
    </div>
  )

  const generalPage = (
    <>
      <fieldset className={s.question}>
        <legend>Product</legend>
        {pills('product', (bits.products || []).map((p) => [p.k, p.label] as const), general.product, (k) => setGen({ product: k }))}
      </fieldset>
      {packright && (
        <p className={styles.hint}>
          Stock &amp; Dispatch: {general.licence ? licenceText(general.licence) : 'Not licensed'}. Its licence is set under Licence and work order.
        </p>
      )}
      {cat.general_qs.filter((q) => !q.product || q.product === general.product).map(question)}
      <label className={styles.field}>
        Retain data from
        <input className={s.date} type="date" value={retainFrom} onChange={(e) => { setRetainFrom(e.target.value); setSaved(null) }} />
        <span className={styles.hint}>
          Data before this date is left out, so testing doesn’t muddy the client’s numbers. Nothing is deleted: move the date earlier and it all comes back.
        </span>
      </label>
      <fieldset className={s.question}>
        <legend>Season start</legend>
        <p className={styles.hint}>What Reports count as this season.</p>
        <div className={s.options}>
          {([['inferred', `Worked out from their data${bits.season?.inferred ? ` (${dateText(bits.season.inferred)})` : ''}`], ['date', 'A date Dataphyll sets']] as const).map(([k, label]) => (
            <label key={k} className={general.season === k ? `${s.option} ${s.chosen}` : s.option}>
              <input type="radio" name="season" checked={general.season === k} onChange={() => setGen({ season: k })} />
              {label}
            </label>
          ))}
        </div>
      </fieldset>
      {general.season === 'date' && (
        <label className={styles.field}>
          This season started on
          <input className={s.date} type="date" value={general.seasonDate} onChange={(e) => setGen({ seasonDate: e.target.value })} />
        </label>
      )}
      <fieldset className={s.question}>
        <legend>Does a worker’s last preassign clock them off?</legend>
        <p className={styles.hint}>
          A preassign is a worker’s name put on an item: scanned in Crew, at a preassign station, or handed over.
        </p>
        <div className={s.options}>
          {([[true, 'Yes'], [false, 'No']] as const).map(([k, label]) => (
            <label key={label} className={general.clockOff === k ? `${s.option} ${s.chosen}` : s.option}>
              <input type="radio" name="clockoff" checked={general.clockOff === k} onChange={() => setGen({ clockOff: k })} />
              {label}
            </label>
          ))}
        </div>
      </fieldset>
    </>
  )

  const groups = [...new Set(cat.uses.map((u) => u.group))]
  const usesPage = (
    <>
      {others.length > 0 && (
        <div className={styles.inline}>
          <label className={styles.field}>
            Set up like another client
            <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">Choose a client</option>
              {others.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <span className={styles.hint}>Careful: this replaces every tick and answer on these pages with theirs. Nothing is saved until you save.</span>
          </label>
          <button
            type="button"
            className={styles.secondary}
            disabled={!copyFrom}
            onClick={() => {
              setAsk({ kind: 'copy' })
            }}
          >
            Copy their setup
          </button>
        </div>
      )}
      {groups.map((g) => (
        <fieldset key={g} className={`${s.question} ${s.section}`}>
          <legend>{g}</legend>
          <div className={s.uses}>
            {cat.uses.filter((u) => u.group === g).map((u) => {
              const locked = u.locked || u.id === 'p_devices'
              return (
                <label key={u.id} className={on(u.id) ? `${s.use} ${s.chosen}` : locked ? `${s.use} ${s.useOff}` : s.use}>
                  <input type="checkbox" checked={on(u.id)} disabled={locked} onChange={(e) => tick(u.id, e.target.checked)} />
                  <span>
                    <strong>{u.label}</strong>
                    <span className={styles.hint}>{u.blurb}</span>
                    {u.note && <span className={s.note}>{u.note}</span>}
                  </span>
                </label>
              )
            })}
          </div>
        </fieldset>
      ))}
    </>
  )

  const modeOn = (m: string) => !cat.team.mode_uses[m] || cat.team.mode_uses[m].some((u) => uses.includes(u))
  const payOn = (p: string) => {
    const pay = answers.pay
    return !Array.isArray(pay) || !cat.team.pay_answers[p] || pay.includes(cat.team.pay_answers[p])
  }
  const reviewPage = (
    <>
      <div>
        <h3 className={s.h3}>Turned on</h3>
        <div className={s.chips}>
          {cat.uses.filter((u) => on(u.id)).map((u) => (
            <span key={u.id} className={s.chip}>{u.label}</span>
          ))}
        </div>
      </div>
      <div>
        <h3 className={s.h3}>What the client’s team setup will offer</h3>
        <p className={styles.hint}>What isn’t turned on stays visible but can’t be chosen.</p>
        <div className={s.preview}>
          <fieldset className={s.question}>
            <legend>App mode</legend>
            {['Harvest', 'Harvest readonly', 'Tagless harvest', 'Receipting', 'Receipt into a bin', 'Supervisor'].map((m) => (
              <span key={m} className={modeOn(m) ? s.offerOn : s.offerOff}>{modeLabel(m)}{!modeOn(m) && ' · not turned on'}</span>
            ))}
          </fieldset>
          <fieldset className={s.question}>
            <legend>Pay basis</legend>
            {[['hourly', 'Hourly'], ['piece_rate', 'Piece rate']].map(([k, label]) => (
              <span key={k} className={payOn(k) ? s.offerOn : s.offerOff}>{label}{!payOn(k) && ' · not turned on'}</span>
            ))}
          </fieldset>
        </div>
      </div>
    </>
  )

  const title = curId === 'general' ? 'Product and modules' : curId === 'uses' ? 'What does this client use?' : curId === 'licence' ? 'Licence and work order' : curId === 'equipment' ? 'Bundled equipment' : curId === 'review' ? 'Review' : steps[cur].label
  const lead =
    curId === 'general' ? 'What this client has bought. It decides which screens exist.' :
    curId === 'uses' ? 'Tick everything they use. Each one ticked asks a few questions next.' :
    curId === 'licence' ? 'What the client is licensed for, as the work order (or its latest amendment) says. Recorded only: nothing is billed or limited from here.' :
    curId === 'equipment' ? 'Every delivery, return, recovery and swap, so we always know what the client has. Serial numbers are optional. Each row saves as it’s added.' :
    curId === 'review' ? 'Check what this client will be offered, then save. Moving between pages keeps your changes; nothing takes effect until you save.' : cat.steps[curId]?.lead

  return (
    <>
      <PageHeader title={`${data.client.name}: Setup`} />
      <PageBody>
        <div className={styles.stack}>
          <div>
            <Link
              to={`/god-mode/clients/${id}`}
              className={styles.secondary}
              onClick={(e) => {
                if (edited) {
                  e.preventDefault()
                  setAsk({ kind: 'leave', then: () => navigate(`/god-mode/clients/${id}`) })
                }
              }}
            >
              ← {data.client.name}
            </Link>
          </div>
          <div className={s.layout}>
            <nav className={s.steps} aria-label="Setup pages">
              {steps.map((st, i) => (
                <button key={st.id} type="button" className={i === cur ? `${s.step} ${s.current}` : s.step} onClick={() => go(i)}>
                  <span className={s.group}>{st.group}</span>
                  {st.label}
                </button>
              ))}
              {data.setup && (
                <p className={styles.hint}>Last saved {dateText(data.setup.updated_at)}{data.setup.updated_by ? ` by ${data.setup.updated_by}` : ''}</p>
              )}
            </nav>
            <section className={styles.card}>
              <div>
                <h2>{title}</h2>
                {lead && <p className={styles.lead}>{lead}</p>}
              </div>
              {error && <p className={styles.error}>{error}</p>}
              {saved && <p className={s.ok} role="status">{saved}</p>}
              {curId === 'general' && generalPage}
              {curId === 'uses' && usesPage}
              {cat.steps[curId] && cat.steps[curId].qs.filter((q) => shown(q, answers)).map(question)}
              {curId === 'licence' && (
                <LicencePage
                  id={id}
                  cat={cat}
                  bound={bound}
                  stockDispatch={{
                    shown: packright,
                    saved: bits.product === 'packright',
                    licence: bits.modules?.stock_dispatch.licence ?? null,
                    licences: bits.licences || [],
                  }}
                  onStockDispatch={tookStockDispatch}
                />
              )}
              {curId === 'equipment' && <EquipmentPage id={id} data={data} on={on} onChange={(d) => setData(d)} />}
              {curId === 'review' && reviewPage}
              <div className={s.footer}>
                <button type="button" className={styles.secondary} disabled={cur === 0} onClick={() => go(cur - 1)}>
                  Back
                </button>
                <div className={styles.inline}>
                  {dirty && <span className={styles.hint}>Unsaved changes</span>}
                  <button type="button" className={last ? styles.primary : styles.secondary} disabled={busy || !dirty} onClick={() => setAsk({ kind: 'save' })}>
                    Save setup
                  </button>
                  {!last && (
                    <button type="button" className={styles.primary} onClick={() => go(cur + 1)}>
                      Next
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
        {ask?.kind === 'unsaved' && (
          <Question
            title="Save your changes first?"
            cancel="Not now"
            ok="Save…"
            onCancel={() => {
              setWaved(JSON.stringify(changes))
              const then = ask.then
              setAsk(null)
              then?.()
            }}
            onOk={() => setAsk({ kind: 'save', then: ask.then })}
          >
            <p className={styles.hint}>Not now keeps them here, unsaved, while you carry on.</p>
          </Question>
        )}
        {ask?.kind === 'save' && (
          <Question
            title="Save this client’s setup?"
            cancel="Go back"
            ok="Save setup"
            busy={busy}
            onCancel={() => setAsk(null)}
            onOk={async () => {
              const then = ask.then
              if (await save()) then?.()
              setAsk(null)
            }}
          >
            <p className={styles.hint}>It takes effect straight away:</p>
            <ul className={s.consequences}>
              {changes.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </Question>
        )}
        {ask?.kind === 'copy' && (
          <Question
            title={`Copy ${others.find((o) => o.id === copyFrom)?.name || 'that client'}’s setup?`}
            cancel="Keep this one"
            ok="Copy their setup"
            onCancel={() => setAsk(null)}
            onOk={() => {
              setAsk(null)
              copy()
            }}
          >
            <p className={styles.hint}>Every tick and answer on these pages is replaced with theirs. Nothing is saved until you save.</p>
          </Question>
        )}
        {ask?.kind === 'leave' && (
          <Question
            title="Leave without saving?"
            cancel="Stay"
            ok="Leave"
            onCancel={() => setAsk(null)}
            onOk={() => {
              const then = ask.then
              setAsk(null)
              then?.()
            }}
          >
            <p className={styles.hint}>Your changes to this setup are lost.</p>
          </Question>
        )}
      </PageBody>
    </>
  )
}

// A question over the page, in the app's dialog (components/layers.tsx): the safe answer
// first and focused, which Escape and a click outside also mean.
function Question({ title, children, cancel, ok, busy = false, onCancel, onOk }: {
  title: string
  children?: ReactNode
  cancel: string
  ok: string
  busy?: boolean
  onCancel: () => void
  onOk: () => void
}) {
  const box = useRef<HTMLDivElement>(null)
  useLayer(true, onCancel, { el: box, modal: true, busy })
  const safe = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    safe.current?.focus()
  }, [])
  return createPortal(
    <div className={dlg.scrim} onClick={(e) => e.target === e.currentTarget && !busy && onCancel()}>
      <div ref={box} className={dlg.box} role="alertdialog" aria-modal="true" aria-labelledby="setup-q">
        <div>
          <p id="setup-q" className={dlg.q}>{title}</p>
          {children}
        </div>
        <div className={dlg.actions}>
          <button ref={safe} type="button" className={dlg.keep} disabled={busy} onClick={onCancel}>
            {cancel}
          </button>
          <button type="button" className={styles.primary} style={{ marginLeft: 'auto' }} disabled={busy} onClick={onOk}>
            {busy ? 'Saving…' : ok}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// The equipment ledger: one block per kind the client's ways of working use,
// each row a delivery, return, recovery or swap with a running total.
const MOVE_SIGN: Record<EquipmentMove, number> = { sent: 1, returned: -1, recovered: -1, swapped: 0 }
const serialList = (v: string) => v.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean)

function EquipmentPage({ id, data, on, onChange }: { id: string; data: SetupResponse; on: (u: string) => boolean; onChange: (d: SetupResponse) => void }) {
  const { user } = useAuth()
  const cat = data.catalogue
  const [draft, setDraft] = useState<
    Record<string, { moved_on: string; move: EquipmentMove; version: string; qty: string; serials: string; serials_out: string; work_order: string; note: string }>
  >({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const blank = (versions: string[], work_order = '') =>
    ({ moved_on: today(), move: 'sent' as EquipmentMove, version: versions[0] || '', qty: '', serials: '', serials_out: '', work_order, note: '' })
  type Ledger = { equipment: EquipmentRow[]; held: Record<string, number>; held_serials: Record<string, string[]> }
  const after = (r: Ledger) => onChange({ ...data, equipment: r.equipment, held: r.held, held_serials: r.held_serials })

  const add = async (kind: string, versions: string[]) => {
    const d = draft[kind] || blank(versions)
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<Ledger>(user, `/api/god-mode/clients/${id}/equipment`, {
        method: 'POST',
        body: {
          kind, version: d.version || null, move: d.move, qty: Number(d.qty), moved_on: d.moved_on,
          serials: serialList(d.serials), serials_out: d.move === 'swapped' ? serialList(d.serials_out) : [],
          work_order: d.work_order || null, note: d.note || null,
        },
      })
      after(r)
      setDraft((x) => ({ ...x, [kind]: blank(versions, d.work_order) }))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }
  const remove = async (row: string) => {
    setBusy(true)
    setError(null)
    try {
      after(await apiJson(user, `/api/god-mode/clients/${id}/equipment/${row}`, { method: 'DELETE' }))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {error && <p className={styles.error}>{error}</p>}
      {cat.equipment.filter((k) => on(k.needs) || data.equipment.some((r) => r.kind === k.id)).map((k) => {
        const rows = data.equipment.filter((r) => r.kind === k.id)
        let total = 0
        const d = draft[k.id] || blank(k.versions)
        const set = (patch: Partial<typeof d>) => setDraft((x) => ({ ...x, [k.id]: { ...d, ...patch } }))
        const known = data.held_serials?.[k.id] || []
        return (
          <fieldset key={k.id} className={s.question}>
            <legend>
              {k.label} <span className={s.chip}>With the client: {data.held[k.id] || 0}</span>
            </legend>
            {known.length > 0 && <p className={s.serials}>Known by serial number: {known.join(', ')}</p>}
            <div className={styles.tableCard}>
              <table className={styles.table}>
                <thead>
                  <tr><th>Date</th><th>Move</th><th>Version</th><th>Qty</th><th>Serial numbers</th><th>Work order, comments</th><th>Total</th><th /></tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    total += MOVE_SIGN[r.move] * r.qty
                    return (
                      <tr key={r.id}>
                        <td>{dateText(r.moved_on)}</td>
                        <td>{cat.moves.find((m) => m.k === r.move)?.label}</td>
                        <td>{r.version || '—'}</td>
                        <td>{r.qty}</td>
                        <td className={s.serials}>
                          {r.move === 'swapped' && r.serials_out?.length ? `Out: ${r.serials_out.join(', ')}` : ''}
                          {r.move === 'swapped' && r.serials_out?.length && r.serials?.length ? <br /> : null}
                          {r.serials?.length ? `${r.move === 'swapped' ? 'In: ' : ''}${r.serials.join(', ')}` : ''}
                        </td>
                        <td>{[r.work_order, r.note].filter(Boolean).join(' · ')}</td>
                        <td><strong>{total}</strong></td>
                        <td>
                          <button type="button" className={s.remove} disabled={busy} onClick={() => remove(r.id)} aria-label="Remove row" title="Entered by mistake">×</button>
                        </td>
                      </tr>
                    )
                  })}
                  <tr>
                    <td><input className={s.cell} type="date" value={d.moved_on} onChange={(e) => set({ moved_on: e.target.value })} aria-label="Date" /></td>
                    <td>
                      <select className={s.cell} value={d.move} onChange={(e) => set({ move: e.target.value as EquipmentMove })} aria-label="Move">
                        {cat.moves.map((m) => <option key={m.k} value={m.k}>{m.label}</option>)}
                      </select>
                    </td>
                    <td>
                      {k.versions.length ? (
                        <select className={s.cell} value={d.version} onChange={(e) => set({ version: e.target.value })} aria-label="Version">
                          {k.versions.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      ) : '—'}
                    </td>
                    <td><input className={s.cell} type="number" min="1" step="1" value={d.qty} onChange={(e) => set({ qty: e.target.value })} aria-label="Quantity" /></td>
                    <td>
                      {d.move === 'swapped' && (
                        <input className={s.cell} type="text" value={d.serials_out} onChange={(e) => set({ serials_out: e.target.value })} aria-label="Serial numbers out" placeholder="Out: FS-1" list={`held-${k.id}`} />
                      )}
                      <input
                        className={s.cell}
                        type="text"
                        value={d.serials}
                        onChange={(e) => set({ serials: e.target.value })}
                        aria-label={d.move === 'swapped' ? 'Serial numbers in' : 'Serial numbers'}
                        placeholder={d.move === 'swapped' ? 'In: FS-9' : 'Optional, e.g. FS-1, FS-2'}
                        list={MOVE_SIGN[d.move] < 0 ? `held-${k.id}` : undefined}
                      />
                      <datalist id={`held-${k.id}`}>{known.map((x) => <option key={x} value={x} />)}</datalist>
                    </td>
                    <td>
                      <input className={s.cell} type="text" value={d.work_order} onChange={(e) => set({ work_order: e.target.value })} aria-label="Work order" placeholder="Work order, e.g. WO-12" maxLength={80} />
                      <input className={s.cell} type="text" value={d.note} onChange={(e) => set({ note: e.target.value })} aria-label="Comments" placeholder="Comments" />
                    </td>
                    <td colSpan={2}>
                      <button type="button" className={styles.primary} disabled={busy || !(Number(d.qty) >= 1)} onClick={() => add(k.id, k.versions)}>
                        Add
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </fieldset>
        )
      })}
    </>
  )
}

// The licence: what the work order (or its latest amendment) says, saved whole
// against the licence as it was opened; what the client uses against it; and
// per equipment kind the units included, with the client and registered as
// Field devices, each device said to be one of the units or not (a loose link).
const DEVICE_TYPE: Record<string, string> = { field_station: 'Weigh station', preassign: 'Preassign station', supervisor: 'Supervisor' }
type LicenceForm = Record<Exclude<keyof LicenceTerms, 'included'>, string> & { included: Record<string, string> }
const toForm = (l: LicenceResponse['licence']): LicenceForm => {
  const str = (v: string | number | null | undefined) => (v === null || v === undefined ? '' : String(v))
  return {
    work_order: str(l?.work_order), term: str(l?.term), starts_on: str(l?.starts_on), ends_on: str(l?.ends_on), sites: str(l?.sites),
    peak_workers: str(l?.peak_workers), currency: str(l?.currency), fee: str(l?.fee), fee_per: str(l?.fee_per),
    extra_worker_fee: str(l?.extra_worker_fee), comingling: str(l?.comingling), note: str(l?.note),
    included: Object.fromEntries(Object.entries(l?.included || {}).map(([k, n]) => [k, String(n)])),
  }
}

type Licence = 'paid' | 'included'

function LicencePage({
  id,
  cat,
  bound,
  stockDispatch: sdNow,
  onStockDispatch,
}: {
  id: string
  cat: SetupResponse['catalogue']
  bound: BoundSource | null
  // shown: the product chosen is PackRight; saved: the client is PackRight (so its route takes a licence).
  stockDispatch: { shown: boolean; saved: boolean; licence: Licence | null; licences: Licence[] }
  onStockDispatch: (l: Licence | null) => void
}) {
  const { user } = useAuth()
  const [data, setData] = useState<LicenceResponse | null>(null)
  const [form, setForm] = useState<LicenceForm>(toForm(null))
  const [sd, setSd] = useState<string>(sdNow.licence || '')
  useEffect(() => setSd(sdNow.licence || ''), [sdNow.licence])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const take = (d: LicenceResponse) => {
    setData(d)
    setForm(toForm(d.licence))
  }
  useEffect(() => {
    let live = true
    apiJson<LicenceResponse>(user, `/api/god-mode/clients/${id}/licence`)
      .then((d) => live && take(d))
      .catch((e) => live && setError(e.message))
    return () => {
      live = false
    }
  }, [user, id])

  if (!data) return error ? <p className={styles.error}>{error}</p> : <p className={styles.lead}>Loading…</p>

  const termsDirty = JSON.stringify(form) !== JSON.stringify(toForm(data.licence))
  const sdDirty = sdNow.shown && sdNow.saved && sd !== (sdNow.licence || '')
  const dirty = termsDirty || sdDirty
  const set = (patch: Partial<LicenceForm>) => {
    setSaved(null)
    setForm((f) => ({ ...f, ...patch }))
  }
  const num = (v: string) => (v.trim() === '' ? null : Number(v))
  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      // Stock & Dispatch is the client's own setting (it turns the module on), saved on its own route.
      if (sdDirty) {
        const next = (sd || null) as Licence | null
        await apiJson(bound, '/api/settings/tenant/licence', { method: 'PUT', body: { licence: next, before: sdNow.licence } })
        onStockDispatch(next)
      }
      if (!termsDirty) {
        setSaved('Licence saved.')
        return
      }
      const d = await apiJson<LicenceResponse>(user, `/api/god-mode/clients/${id}/licence`, {
        method: 'PUT',
        body: {
          work_order: form.work_order || null, term: form.term || null, starts_on: form.starts_on || null, ends_on: form.ends_on || null,
          sites: num(form.sites), peak_workers: num(form.peak_workers), currency: form.currency || null, fee: num(form.fee),
          fee_per: form.fee_per || null, extra_worker_fee: num(form.extra_worker_fee), comingling: form.comingling || null, note: form.note || null,
          included: Object.fromEntries(Object.entries(form.included).filter(([, n]) => n.trim() !== '').map(([k, n]) => [k, Number(n)])),
          before: data.licence?.updated_at ?? null,
        },
      })
      take(d)
      setSaved('Licence saved.')
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'STALE') setError('Someone else saved this licence since you opened it. Reload the page to see theirs.')
      else setError(e.message)
    } finally {
      setBusy(false)
    }
  }
  const link = async (device: string, serial: string | null) => {
    setBusy(true)
    setError(null)
    try {
      setData(await apiJson<LicenceResponse>(user, `/api/god-mode/clients/${id}/devices/${device}/unit`, { method: 'PUT', body: { serial } }))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const lic = data.licence
  const { usage } = data
  const busiest30 = usage.days.reduce<{ date: string; people: number } | null>((a, d) => (d.people && (!a || d.people > a.people) ? d : a), null)
  const over = (n: number, limit: number | null | undefined) => limit !== null && limit !== undefined && n > limit
  const licenceOpts = (list: Licence[]) => [
    <option key="" value="">Not licensed</option>,
    ...list.map((l) => <option key={l} value={l}>{licenceText(l)}</option>),
  ]
  const opts = (list: { k: string; label: string }[]) => [
    <option key="" value="">—</option>,
    ...list.map((o) => <option key={o.k} value={o.k}>{o.label}</option>),
  ]

  return (
    <>
      {error && <p className={styles.error}>{error}</p>}
      {saved && <p className={s.ok} role="status">{saved}</p>}

      <fieldset className={s.question}>
        <legend>Against the licence</legend>
        <dl className={styles.facts}>
          <div>
            <dt>Active Sites</dt>
            <dd className={over(usage.sites, lic?.sites) ? styles.breach : undefined}>
              {usage.sites}{lic?.sites != null ? ` of ${lic.sites} licensed` : ''}
            </dd>
          </div>
          <div>
            <dt>Busiest day this term</dt>
            <dd className={usage.term.busiest && over(usage.term.busiest.people, lic?.peak_workers) ? styles.breach : undefined}>
              {usage.term.busiest ? `${usage.term.busiest.people} people, ${dateText(usage.term.busiest.date)}` : 'Nobody clocked on yet'}
              {lic?.peak_workers != null ? ` (licensed ${lic.peak_workers})` : ''}
            </dd>
          </div>
          {usage.term.days_over != null && (
            <div>
              <dt>Days over the peak</dt>
              <dd className={usage.term.days_over ? styles.breach : undefined}>{usage.term.days_over}</dd>
            </div>
          )}
          <div>
            <dt>Busiest of the last 30 days</dt>
            <dd>{busiest30 ? `${busiest30.people} people, ${dateText(busiest30.date)}` : 'Nobody clocked on'}</dd>
          </div>
        </dl>
        <p className={styles.hint}>
          People clocked on to a team other than induction, counted once a day. The term runs {dateText(usage.term.from)} to {dateText(usage.term.to)}
          {lic?.starts_on ? '' : ' (no start date recorded, so the last year)'}.
        </p>
      </fieldset>

      <fieldset className={s.question}>
        <legend>Licensed modules</legend>
        <div className={s.terms}>
          <label className={styles.field}>
            Co-mingling (sharing tags)
            <select value={form.comingling} onChange={(e) => set({ comingling: e.target.value })} aria-label="Co-mingling licence">
              {licenceOpts(['paid', 'included'])}
            </select>
            <span className={styles.hint}>Needed for this client to share its tags with others (God Mode › Tags). The clients it shares with need not be.</span>
          </label>
          {sdNow.shown && (
            <label className={styles.field}>
              Stock &amp; Dispatch
              <select
                value={sd}
                onChange={(e) => {
                  setSaved(null)
                  setSd(e.target.value)
                }}
                disabled={!sdNow.saved}
                aria-label="Stock & Dispatch licence"
              >
                {licenceOpts(sdNow.licences)}
              </select>
              <span className={styles.hint}>
                {sdNow.saved
                  ? 'Pallets, customer orders, truck loads and draft Xero invoices. The module turns on or off for the client when this is saved.'
                  : 'Save Product and modules (Dataphyll PackRight) first.'}
              </span>
            </label>
          )}
        </div>
      </fieldset>

      <fieldset className={s.question}>
        <legend>Terms</legend>
        <div className={s.terms}>
          <label className={styles.field}>
            Work order or amendment
            <input value={form.work_order} maxLength={80} onChange={(e) => set({ work_order: e.target.value })} placeholder="e.g. WO-12 amendment 1" />
          </label>
          <label className={styles.field}>
            Term
            <select value={form.term} onChange={(e) => set({ term: e.target.value })}>{opts(cat.licence.terms)}</select>
          </label>
          <label className={styles.field}>
            Starts
            <input type="date" value={form.starts_on} onChange={(e) => set({ starts_on: e.target.value })} />
          </label>
          <label className={styles.field}>
            Ends
            <input type="date" value={form.ends_on} onChange={(e) => set({ ends_on: e.target.value })} />
          </label>
          <label className={styles.field}>
            Licensed Sites
            <input type="number" min="0" step="1" value={form.sites} onChange={(e) => set({ sites: e.target.value })} />
          </label>
          <label className={styles.field}>
            Peak workers per day
            <input type="number" min="0" step="1" value={form.peak_workers} onChange={(e) => set({ peak_workers: e.target.value })} />
          </label>
          <label className={styles.field}>
            Currency
            <select value={form.currency} onChange={(e) => set({ currency: e.target.value })}>
              {opts(cat.licence.currencies.map((c) => ({ k: c, label: c })))}
            </select>
          </label>
          <label className={styles.field}>
            Licence fee
            <input type="number" min="0" step="0.01" value={form.fee} onChange={(e) => set({ fee: e.target.value })} />
          </label>
          <label className={styles.field}>
            Fee is
            <select value={form.fee_per} onChange={(e) => set({ fee_per: e.target.value })}>{opts(cat.licence.fee_per)}</select>
          </label>
          <label className={styles.field}>
            Fee per extra worker
            <input type="number" min="0" step="0.01" value={form.extra_worker_fee} onChange={(e) => set({ extra_worker_fee: e.target.value })} />
          </label>
          <label className={`${styles.field} ${s.wide}`}>
            Notes
            <textarea value={form.note} maxLength={1000} onChange={(e) => set({ note: e.target.value })} />
          </label>
        </div>
      </fieldset>

      <fieldset className={s.question}>
        <legend>Hardware</legend>
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr><th>Equipment</th><th>Included in the subscription</th><th>With the client</th><th>Registered in Control</th></tr>
            </thead>
            <tbody>
              {data.units.map((u) => (
                <tr key={u.kind}>
                  <td>{u.label}</td>
                  <td>
                    <input
                      className={s.cell}
                      type="number"
                      min="0"
                      step="1"
                      value={form.included[u.kind] ?? ''}
                      onChange={(e) => set({ included: { ...form.included, [u.kind]: e.target.value } })}
                      aria-label={`${u.label} included`}
                    />
                  </td>
                  <td>{u.held}</td>
                  <td>{u.registered ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.hint}>Deliveries, returns and swaps are recorded under Bundled equipment.</p>
      </fieldset>

      <div className={styles.inline}>
        {lic && (
          <span className={styles.hint}>
            Last saved {dateText(lic.updated_at)}{lic.updated_by ? ` by ${lic.updated_by}` : ''} · {lic.history.length} version{lic.history.length === 1 ? '' : 's'}
          </span>
        )}
        {dirty && <span className={styles.hint}>Unsaved changes</span>}
        <button type="button" className={styles.primary} disabled={busy || !dirty} onClick={save}>
          {busy ? 'Saving…' : 'Save licence'}
        </button>
      </div>

      {data.devices.length > 0 && (
        <fieldset className={s.question}>
          <legend>Field devices and their units</legend>
          <div className={styles.tableCard}>
            <table className={styles.table}>
              <thead>
                <tr><th>Device</th><th>Type</th><th>Dataphyll unit</th></tr>
              </thead>
              <tbody>
                {data.devices.map((d) => {
                  const taken = (x: string) => data.devices.some((o) => o.id !== d.id && o.active && o.unit?.kind === d.kind && o.unit?.serial === x)
                  const free = d.kind ? (data.held_serials[d.kind] || []).filter((x) => x === d.unit?.serial || !taken(x)) : []
                  return (
                    <tr key={d.id}>
                      <td>
                        {d.name || d.device_id}
                        {d.name && <span className={styles.hint}> · {d.device_id}</span>}
                        {!d.active && <span className={styles.hint}> · switched off</span>}
                      </td>
                      <td>{(d.device_type && DEVICE_TYPE[d.device_type]) || d.device_type || '—'}</td>
                      <td>
                        {d.kind ? (
                          <>
                            <select
                              className={s.cell}
                              value={d.unit?.serial || ''}
                              disabled={busy}
                              onChange={(e) => link(d.id, e.target.value || null)}
                              aria-label={`Unit for ${d.name || d.device_id}`}
                            >
                              <option value="">{free.length || d.unit ? 'Not said' : 'No serial numbers recorded'}</option>
                              {d.unit && !d.unit.with_client && <option value={d.unit.serial}>{d.unit.serial}</option>}
                              {free.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                            {d.unit && !d.unit.with_client && <span className={styles.breach}> Unit returned</span>}
                          </>
                        ) : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className={styles.hint}>Optional. A unit swapped out or taken back stays against its device, shown as returned, until changed here.</p>
        </fieldset>
      )}
    </>
  )
}
